// src/components/modules/PositionPrompt.jsx
// A canonical fork rendered at the end of its section page: commit to a
// position with one tap. The lens line appears only after choosing — the
// card never leads the witness. Once committed, the reader also sees
// where OTHER readers landed (anonymous counters; readerSignals.js).
import React from 'react';
import { Link } from 'react-router-dom';
import { Compass, ArrowRight } from 'lucide-react';
import { usePositions } from '../../hooks/usePositions';
import { usePositionAggregate } from '../../hooks/useReaderSignals';
import WhereReadersLand from './WhereReadersLand';

const PositionPrompt = ({ point }) => {
  const { positions, setPosition } = usePositions();
  const answer = positions[point.id];
  const chosen = answer && point.options.find(o => o.id === answer.choice);
  const aggregate = usePositionAggregate(point.id, answer?.choice || null);

  return (
    <div className="mt-6 rounded-xl border border-violet-500/30 bg-violet-500/5 p-6">
      <div className="flex items-center gap-2 mb-1">
        <Compass className="w-5 h-5 text-violet-300" />
        <span className="text-xs font-semibold tracking-wide uppercase text-violet-300">Take a position</span>
      </div>
      <h4 className="text-lg font-bold text-white mb-2">{point.title}</h4>
      <p className="text-slate-300 mb-4">{point.question}</p>
      <div className="flex flex-col gap-2" role="radiogroup" aria-label={point.question}>
        {point.options.map(option => {
          const selected = answer?.choice === option.id;
          return (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setPosition(point.id, option.id)}
              className={`text-left px-4 py-3 rounded-lg border transition-all ${
                selected
                  ? 'border-violet-400 bg-violet-500/20 text-white'
                  : 'border-slate-600 bg-slate-800/40 text-slate-200 hover:border-violet-400/60 hover:bg-violet-500/10'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {chosen && (
        <div className="mt-4 pt-4 border-t border-violet-500/20" aria-live="polite">
          <p className="text-sm text-violet-200 mb-2">{chosen.lens}</p>
          <WhereReadersLand point={point} aggregate={aggregate} ownChoice={answer.choice} />
          <Link
            to="/positions"
            className="mt-3 inline-flex items-center gap-1 text-sm text-violet-300 hover:text-white transition-colors"
          >
            See your philosophy so far
            <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-xs text-slate-400 mt-2">You can change your answer any time; philosophers do.</p>
        </div>
      )}
    </div>
  );
};

export default PositionPrompt;
